// What kind of device the site runs on: phone, tablet or desktop, decided once at start-up. Each gets its own
// profile: how much pixel density to draw, how big the textures may be on the GPU, which controls to show.
//
// Detection, strongest signal first:
//   1. User-Agent Client Hints (`navigator.userAgentData.mobile`, Chromium): true means a phone.
//   2. The user agent string: iPhone / Android "Mobile" / Windows Phone are phones; iPad, Android without "Mobile"
//      and "Tablet" are tablets. iPadOS 13+ reports itself as a Mac, so a "Macintosh" with a touch screen is an iPad.
//   3. The input hardware: a coarse primary pointer with no hover is a touch device; its shorter screen side
//      decides phone (< 600 CSS px) or tablet. A fine pointer or hover is a desktop (touch-screen laptops included).
// The result is written to <html data-device="phone|tablet|desktop"> (and the `touch` class) for CSS.

import { SAFE } from './system/safe';

export type DeviceKind = 'phone' | 'tablet' | 'desktop';

export interface DeviceProfile {
  kind: DeviceKind;
  touch: boolean;
  /** Pixel-density cap for the city (big scene) and for the studio (small static room). */
  cityPixelRatio: number;
  studioPixelRatio: number;
  /** Largest texture edge to keep on the GPU; bigger baked atlases are downscaled after loading. */
  maxTexture: number;
  /** Bloom in the studio (a full-screen post pass; skipped on phones to save fill rate and battery). */
  bloom: boolean;
  /** False when the browser cannot create a WebGL context at all: the site then shows a static card instead. */
  webgl: boolean;
  /** Safe mode: the lightest settings that still look right (system/safe.ts). */
  safe?: boolean;
}

type UAData = { mobile?: boolean };

function classify(): DeviceKind {
  const nav = navigator as Navigator & { userAgentData?: UAData };
  const ua = nav.userAgent;
  const touchPoints = nav.maxTouchPoints || 0;
  if (/iPad|Tablet|PlayBook|Silk|Kindle/i.test(ua)) return 'tablet';
  if (/Macintosh/i.test(ua) && touchPoints > 1) return 'tablet';   // iPadOS asks for the desktop site
  if (nav.userAgentData?.mobile === true || /iPhone|iPod|Windows Phone|Android.+Mobile|Mobi/i.test(ua)) return 'phone';
  if (/Android/i.test(ua)) return 'tablet';
  const coarse = matchMedia('(pointer: coarse)').matches && !matchMedia('(hover: hover)').matches;
  if (coarse) return Math.min(screen.width, screen.height) < 600 ? 'phone' : 'tablet';
  return 'desktop';
}

function hasWebGL() {
  try {
    const c = document.createElement('canvas');
    return Boolean(c.getContext('webgl2') ?? c.getContext('webgl'));
  } catch {
    return false;
  }
}

function profileFor(kind: DeviceKind): DeviceProfile {
  const webgl = hasWebGL();
  const dpr = devicePixelRatio || 1;
  const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 8;
  const touch = kind !== 'desktop' || ((navigator.maxTouchPoints || 0) > 0 && matchMedia('(pointer: coarse)').matches);
  if (kind === 'phone') return { kind, touch, cityPixelRatio: Math.min(dpr, 1.5), studioPixelRatio: Math.min(dpr, 2), maxTexture: 3072, bloom: false, webgl };
  if (kind === 'tablet') return { kind, touch, cityPixelRatio: Math.min(dpr, 1.5), studioPixelRatio: Math.min(dpr, 2), maxTexture: memory <= 4 ? 2048 : 4096, bloom: true, webgl };
  return { kind, touch, cityPixelRatio: Math.min(dpr, 1.7), studioPixelRatio: Math.min(dpr, 2), maxTexture: 4096, bloom: true, webgl };
}

// safe mode (system/safe.ts: chosen by the visitor, or after repeated crashes): one pixel per CSS pixel at most, no
// bloom or shadows, 2K textures, so a struggling GPU or a low-memory phone keeps running
function safe(p: DeviceProfile): DeviceProfile {
  if (!SAFE.on) return p;
  return { ...p, safe: true, cityPixelRatio: Math.min(p.cityPixelRatio, 1), studioPixelRatio: Math.min(p.studioPixelRatio, 1.25), maxTexture: 2048, bloom: false };
}

// ?device=phone|tablet|desktop forces a profile (for testing on a desktop browser)
const forced = new URLSearchParams(location.search).get('device');
export const DEVICE: DeviceProfile = safe(profileFor(forced === 'phone' || forced === 'tablet' || forced === 'desktop' ? forced : classify()));

document.documentElement.dataset.device = DEVICE.kind;
document.documentElement.classList.toggle('touch', DEVICE.touch);
